import { useCallback, useEffect } from 'react';

import { useLocation, useNavigate, useParams } from '@tanstack/react-router';

import { useFormInputMultipleEmails } from '@/_containers';
import { useTicketDetailsFormDefinition } from '@/_containers/support/shared/form-context';
import { resetFormState, snackbar, useEdit, useInvalidate, usePostAuditLog, useProfileCrudActions } from '@/_shared';

import { useRevisionHistory } from './use-revision-history';

import { useDeleteMutation, useGetContact, useUpdateMutation } from '../apis';
import { contactFormToUpdate, contactQueryToForm } from '../converters';
import { useFormDefinition } from '../form-context';
import type { ContactFormSchema, ContactQuerySchema, ContactUpdateSchema } from '../schemas';

export function usePage() {
  const { isEmailsPending, handleEmails } = useFormInputMultipleEmails();
  const nav = useNavigate();
  const { state } = useLocation();
  const edit = useEdit(state.isFromNew);
  const form = useFormDefinition();
  const { id } = useParams({ strict: false });
  usePostAuditLog({ endpoint: 'contacts', id: `${id}` });

  const { setHasToInvalidate, removeQuery } = useInvalidate(['CONTACT', id ?? '']);
  const ticketDetailsForm = useTicketDetailsFormDefinition();

  const contact = useGetContact(`${id}`);
  const updateMutation = useUpdateMutation(`${id}`);
  const { isPending: formIsPending } = updateMutation;
  const isPending = isEmailsPending || formIsPending;

  const deleteMutation = useDeleteMutation();

  const handleSuccessSideEffects = useCallback(() => {
    removeQuery(['CONTACTS']);
    setHasToInvalidate.setTrue();
  }, [removeQuery, setHasToInvalidate]);

  const { onUpdate, onPartialUpdate, onDelete, isDeleting, isSuccess } = useProfileCrudActions<
    ContactFormSchema,
    ContactUpdateSchema,
    { data: ContactQuerySchema }
  >({
    form,
    edit,
    formToMutation: contactFormToUpdate,
    updateMutation,
    deleteMutation,
    deleteVariables: { id: `${id}` },
    updateSuccessMessage: 'Contact has been updated.',
    deleteSuccessMessage: 'Contact has been deleted.',
    onSuccessSideEffects: handleSuccessSideEffects,
    onDeleteSuccess: () => {
      nav({ to: '/contacts' });
    },
  });

  const onSubmit = useCallback(
    async (data: ContactFormSchema) => {
      const handledEmails = await handleEmails(data.emails);
      if (handledEmails === false) {
        snackbar.error('Error while updating emails.');
        return;
      }

      onUpdate(contactFormToUpdate({ ...data, emails: handledEmails }));
    },
    [handleEmails, onUpdate]
  );

  const revisionHistory = useRevisionHistory(form, onUpdate);

  useEffect(() => {
    const { data: response } = contact;
    if (response) {
      resetFormState(form, contactQueryToForm(response.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.data]);

  console.log(form.formState.errors);
  console.log(form.getValues());

  return {
    id,
    form,
    edit,
    isPending: isPending || isDeleting,
    isSuccess,
    isDeleting,
    onSubmit,
    onDelete,
    onPartialUpdate,
    revisionHistory,
    isReverting: contact.isFetching || isPending || isDeleting,
    ticketDetailsForm,
  };
}

export type UsePageType = ReturnType<typeof usePage>;
